import React, { useEffect, useState } from "react";
import { Box, Stack, CircularProgress } from "@mui/material";
import { getAssetTransfers } from "../../../../../services/asset";
import { tokens } from "../../../../../theme";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");
const personName = (p) => [p?.fname, p?.lname].filter(Boolean).join(" ") || "—";

/**
 * Every time this asset changed legal entity, and why.
 *
 * One component for both places it is shown: inside the move dialog, where
 * somebody about to move an asset for the third time should see the first two
 * without going looking; and on the asset's own record, which is where anybody
 * asking "why is this on the other company's books?" will actually be.
 *
 * Owns its own fetch rather than taking rows as a prop, so a caller only needs
 * the asset id — and a failure to load is reported in place rather than
 * bubbling up and taking a screen down over a sidebar panel.
 */
const AssetTransferHistory = ({ assetId, dense = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!assetId) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    setLoading(true);
    setFailed(false);
    getAssetTransfers(assetId)
      .then((res) => {
        if (!active) return;
        if (res?.success === false) {
          setFailed(true);
          return;
        }
        setRows(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => active && setFailed(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [assetId]);

  if (loading) return <CircularProgress size={18} />;

  if (failed) {
    return (
      <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
        Couldn't load the ownership history.
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
        This asset has not moved between legal entities.
      </Box>
    );
  }

  return (
    <Stack spacing={dense ? 1 : 1.5}>
      {rows.map((t) => (
        <Box key={t._id} sx={{ borderLeft: `2px solid ${tokens.border}`, pl: "0.75rem" }}>
          <Box sx={{ fontSize: "0.8125rem", color: tokens.text }}>
            {t.fromLegalUnit?.name || "No entity"} → <b>{t.toLegalUnit?.name}</b>
          </Box>
          {/* The reason is the point of the record, so it reads as the content
              rather than as metadata under the names. */}
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{t.reason}</Box>
          {t.note ? (
            <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted, fontStyle: "italic" }}>
              {t.note}
            </Box>
          ) : null}
          <Box sx={{ fontSize: "0.75rem", color: tokens.textMuted }}>
            {fmtDate(t.effectiveDate)} · {personName(t.createdBy)}
          </Box>
        </Box>
      ))}
    </Stack>
  );
};

export default AssetTransferHistory;
